import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useModuleDefinition } from '@platform/modularity/useModuleDefinition';
import { ErrorBanner, Spinner } from '@platform/ui';
import { MODULE_RETURN, routes } from '@platform/core/lib/routes';
import { useWorkspaceParams } from '@platform/hooks/useWorkspaceParams';
import { lastModulePage } from '@platform/engine/list/landing/lastModulePage';
import { useLandingReset } from '@platform/engine/list/landing/useLandingReset';
import { mainRecordTypeOf, tableEntryKeyOf } from '@platform/engine/menu/moduleSections';

/**
 * /:group/:module — відкриття модуля з верхнього меню, закріплених чи «Усіх модулів» (UIUX layout/left-menu, прототип v4 openModule):
 * головна таблиця на типовому пункті «Усі …» (tableEntryKeyOf) у поданні «Таблиця» без фільтрів — стан пункту скидається (useLandingReset),
 * далі перехід із заміною історії. З `?return=1` (routes.moduleReturn: крихта модуля, «← Модуль», «Відкрити модуль» налаштувань — прототип
 * page:'module' без openModule) — повернення без скидання: остання сторінка модуля цього сеансу (lastModulePage), інакше головна таблиця.
 */
export function ModulePage() {
  const { ws, module: moduleKey } = useWorkspaceParams();
  const [params] = useSearchParams();
  const back = params.has(MODULE_RETURN);
  const navigate = useNavigate();
  const reset = useLandingReset();
  const { data, isLoading, error } = useModuleDefinition(moduleKey);
  const main = data ? mainRecordTypeOf(data) : undefined;
  useEffect(() => {
    if (!main) return;
    const table = routes.list(moduleKey, main.key, 'table');
    if (back) {
      navigate(lastModulePage(ws, moduleKey) ?? table, { replace: true });
      return;
    }
    reset(ws, moduleKey, main.key, tableEntryKeyOf(main));
    navigate(table, { replace: true });
  }, [main, ws, moduleKey, back, reset, navigate]);
  if (isLoading || main) return <Spinner />;
  return <ErrorBanner error={error ?? { message: 'No record types' }} />;
}
